// Darstellung von Layouts im DOM. Gemeinsam für Player (Engine) und Designer-Vorschau (Manager),
// damit die Vorschau genau so aussieht wie auf dem Bildschirm. Verhalten wie prototyp/public/widgets.js.
import { FACE_PRESETS, buildFace, faceColors, handAngles, isAnalogFace, runHands, safeTimeZone, setHands } from './analog.js';
import { rtspCamera } from './camera.js';
import { DATA_CSS, DATA_WIDGETS } from './data-widgets.js';
import { bindingToText, isDataElementType, parseDataSet } from './data.js';
import { clear, cssSafe, cssUrl, h, placeholder } from './dom.js';
import { WEATHER, weatherCache } from './weather.js';
import { PRAYER_CSS, renderPrayer } from './prayer-render.js';
export const CSS = `
    .wl-canvas { position: relative; isolation: isolate; container-type: size; overflow: hidden; background-size: cover; background-position: center;
      font-family: "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #fff; }
    .wl-frame { position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%); isolation: isolate; container-type: size;
      overflow: hidden; background-size: cover; background-position: center; }
    .wl-el { position: absolute; overflow: hidden; box-sizing: border-box; }
    .wl-fill { width: 100%; height: 100%; display: block; border: 0; }
    .wl-center { width: 100%; height: 100%; display: flex; flex-direction: column; justify-content: center; padding: 0 2cqh; box-sizing: border-box; }
    .wl-clamp { display: -webkit-box; -webkit-box-orient: vertical; overflow: hidden; }
    .wl-placeholder { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; text-align: center;
      background: repeating-linear-gradient(45deg, #2a2a2a 0 2cqh, #333 2cqh 4cqh); color: #bbb; font-size: 3cqh; padding: 2cqh; box-sizing: border-box; }
    .wl-slides { position: relative; width: 100%; height: 100%; }
    .wl-slides img { position: absolute; inset: 0; width: 100%; height: 100%; opacity: 0; transition: opacity 1s; }
    .wl-slides img.on { opacity: 1; }
    .wl-ticker { width: 100%; height: 100%; display: flex; align-items: center; white-space: nowrap; overflow: hidden; }
    .wl-ticker span { display: inline-block; padding-left: 100%; animation: wl-scroll linear infinite; }
    @keyframes wl-scroll { from { transform: translateX(0); } to { transform: translateX(-100%); } }
    .wl-weather { width: 100%; height: 100%; display: flex; flex-direction: column; justify-content: center; align-items: center; gap: .4em; }
    .wl-weather .now { display: flex; align-items: center; gap: .3em; font-weight: 700; }
    .wl-weather .place { opacity: .8; font-size: .45em; }
    .wl-weather .days { display: flex; gap: 1em; font-size: .4em; opacity: .9; }
    .wl-weather .days div { text-align: center; }
    .wl-sched { width: 100%; height: 100%; padding: 2cqh 2.5cqh; box-sizing: border-box; }
    .wl-sched h2 { margin: 0 0 .6em; color: #f5c518; text-transform: uppercase; font-size: 1.4em; }
    .wl-sched table { width: 100%; border-collapse: collapse; }
    .wl-sched td { padding: .35em .3em; border-bottom: 1px solid rgba(255,255,255,.15); }
    .wl-sched td.t { color: #f5c518; font-weight: 700; white-space: nowrap; width: 1%; }
    .wl-sched td.m { opacity: .7; text-align: right; }
    .wl-sched tr.past { opacity: .35; }
    .wl-analog { width: 100%; height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center;
      padding: 1.5cqh; box-sizing: border-box; }
    .wl-analog svg { flex: 1 1 0; min-height: 0; width: 100%; display: block; }
    .wl-analog .cap { max-width: 100%; margin-top: .35em; line-height: 1.2; text-align: center; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .wl-embed { position: absolute; inset: 0; container-type: size; overflow: hidden; }
    .wl-cam-msg { position: absolute; left: 0; right: 0; bottom: 0; padding: 1cqh 2cqh; background: rgba(0,0,0,.6); color: #ddd;
      font-size: 2.4cqh; text-align: center; pointer-events: none; }
  `;
export function ensureStyles(doc) {
    if (doc.getElementById('wl-style'))
        return;
    const st = doc.createElement('style');
    st.id = 'wl-style';
    st.textContent = CSS + DATA_CSS + PRAYER_CSS;
    (doc.head ?? doc.documentElement).appendChild(st);
}
/** Höchstens so viele Ebenen eingebetteter Vorlagen (Schutz vor Endlosschleifen, der Server prüft zusätzlich). */
export const MAX_EMBED_DEPTH = 4;
const textStyle = (el) => `color:${cssSafe(el.color || '#fff')};font-size:${Number(el.size) || 6}cqh;text-align:${cssSafe(el.align || 'left')};` +
    `font-weight:${el.bold ? 700 : 400};`;
/** Schriftfamilien der festen Liste (FONT_FAMILIES) mit Systemschriften als Ausweichlösung. */
export const FONT_STACKS = {
    sans: '"Inter Variable", Inter, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    display: '"Inter Display", "Inter Variable", Inter, "Segoe UI Variable Display", "Segoe UI", Roboto, Arial, sans-serif',
    serif: 'Georgia, Charter, "Bitstream Charter", "Iowan Old Style", "Liberation Serif", "Times New Roman", serif',
    condensed: '"Bahnschrift", "Roboto Condensed", "Arial Narrow", "Inter Variable", Inter, sans-serif',
    mono: 'ui-monospace, "SFMono-Regular", "Cascadia Mono", "DejaVu Sans Mono", Menlo, Consolas, monospace',
};
/** Zusätzliche Typografie (nur gesetzte Angaben, damit bestehende Folien unverändert aussehen). */
function typoStyle(el) {
    let out = '';
    if (el.font && FONT_STACKS[el.font])
        out += `font-family:${FONT_STACKS[el.font]};`;
    if (el.font === 'condensed')
        out += 'font-stretch:condensed;';
    if (el.weight)
        out += `font-weight:${Math.round(Number(el.weight))};`;
    if (el.italic)
        out += 'font-style:italic;';
    if (el.spacing !== undefined)
        out += `letter-spacing:${Number(el.spacing)}em;`;
    if (el.upper)
        out += 'text-transform:uppercase;';
    return out;
}
const fitStyle = (fit) => `object-fit:${cssSafe(fit || 'cover')}`;
/** Abspiel-Erfassung: Medium eines Elements melden (nur Medien aus dem Manifest, nicht in der Designer-Vorschau). */
function reportZone(ctx, zone, asset, change = false) {
    // change: Wechsel einer Bilderfolge – auch ohne Medium melden (beendet das vorige Medium der Zone)
    if (ctx.preview || !ctx.onZoneMedia || (!asset && !change))
        return;
    try {
        ctx.onZoneMedia(zone || 'element', asset ?? null);
    }
    catch {
        // Erfassung darf die Darstellung nie stören
    }
}
function mediaSrc(ctx, el) {
    if (el.asset)
        return ctx.resolveAsset(el.asset);
    return el.src || '';
}
function fmtParts(ctx, at, o) {
    return new Date(at).toLocaleString(ctx.locale, { ...o, ...(ctx.timeZone ? { timeZone: ctx.timeZone } : {}) });
}
/** "HH:mm" der aktuellen Zeit in der Standort-Zeitzone. */
function hhmmNow(ctx) {
    return fmtParts(ctx, ctx.now(), { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
}
/** Bausteine; jeder liefert optional eine Aufräumfunktion. */
export const WIDGET_TYPES = {
    text: {
        label: 'Text',
        defaults: { text: 'Dein Text', size: 7, color: '#ffffff', align: 'center', bold: true },
        render(box, el, { doc }) {
            clear(box);
            const just = el.valign === 'top' ? 'flex-start' : el.valign === 'bottom' ? 'flex-end' : '';
            box.appendChild(h(doc, 'div', {
                class: 'wl-center',
                style: `${textStyle(el)}${typoStyle(el)}white-space:pre-line;line-height:${Number(el.lineHeight) || 1.15}` +
                    (just ? `;justify-content:${just}` : '') + (el.pad !== undefined ? `;padding:${Number(el.pad)}cqh` : ''),
                text: el.text ?? '',
            }));
        },
    },
    image: {
        label: 'Bild',
        defaults: { src: '', fit: 'cover' },
        render(box, el, ctx) {
            const src = mediaSrc(ctx, el);
            if (!src)
                return placeholder(ctx.doc, box, 'Bild wählen');
            reportZone(ctx, el.id, el.asset);
            clear(box);
            box.appendChild(h(ctx.doc, 'img', { class: 'wl-fill', style: fitStyle(el.fit), attrs: { src } }));
        },
    },
    slideshow: {
        label: 'Bilder-Slideshow',
        defaults: { srcs: [], interval: 6, fit: 'cover' },
        render(box, el, ctx) {
            const ms = Math.max(2, Number(el.interval) || 6) * 1000;
            const slides = [...(el.assets ?? []).map((asset) => ({ src: ctx.resolveAsset(asset), ms, asset })), ...(el.srcs ?? []).map((src) => ({ src, ms }))];
            if (!slides.length)
                return placeholder(ctx.doc, box, 'Bilder für die Slideshow wählen');
            return slideshow(ctx, box, slides, el.fit, el.id);
        },
    },
    video: {
        label: 'Video',
        defaults: { src: '', fit: 'cover' },
        render(box, el, ctx) {
            const src = mediaSrc(ctx, el);
            if (!src)
                return placeholder(ctx.doc, box, 'Video wählen');
            reportZone(ctx, el.id, el.asset);
            clear(box);
            const v = h(ctx.doc, 'video', {
                class: 'wl-fill',
                style: fitStyle(el.fit),
                attrs: { src, muted: true, loop: true, autoplay: true, playsinline: true },
            });
            v.muted = true;
            box.appendChild(v);
            v.play?.()?.catch?.(() => { });
        },
    },
    clock: {
        label: 'Uhr',
        defaults: { face: 'digital', mode: 'both', seconds: false, secondHand: true, size: 9, color: '#ffffff', align: 'center', bold: true },
        render(box, el, ctx) {
            const { doc } = ctx;
            // Eigene Zeitzone des Bausteins (Weltuhr) vor der Standort-Zeitzone.
            const timeZone = safeTimeZone(el.timeZone) ?? ctx.timeZone;
            if (isAnalogFace(el.face))
                return analogClock(box, el, { ...ctx, timeZone });
            const zctx = { ...ctx, timeZone };
            clear(box);
            const tm = h(doc, 'div', { class: 'tm' });
            const dt = h(doc, 'div', { class: 'dt', style: 'font-size:.4em;font-weight:400;opacity:.85' });
            box.appendChild(h(doc, 'div', { class: 'wl-center', style: `${textStyle(el)}line-height:1.1` }, [tm, dt]));
            const tick = () => {
                const at = ctx.now();
                tm.textContent =
                    el.mode === 'date'
                        ? ''
                        : fmtParts(zctx, at, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', ...(el.seconds ? { second: '2-digit' } : {}) });
                dt.textContent = el.mode === 'time' ? '' : fmtParts(zctx, at, { weekday: 'long', day: 'numeric', month: 'long' });
                if (el.mode === 'date')
                    dt.style.fontSize = '1em';
            };
            tick();
            const t = setInterval(tick, 1000);
            return () => clearInterval(t);
        },
    },
    weather: {
        label: 'Wetter',
        defaults: { place: '', lat: null, lon: null, days: 3, size: 9, color: '#ffffff' },
        render(box, el, ctx) {
            const { doc } = ctx;
            if (el.lat == null || el.lon == null)
                return placeholder(doc, box, 'Ort für das Wetter wählen');
            const lat = el.lat;
            const lon = el.lon;
            clear(box);
            const wrap = h(doc, 'div', { class: 'wl-weather', style: textStyle(el) });
            box.appendChild(wrap);
            const draw = (d) => {
                clear(wrap);
                if (!d || !d.current) {
                    wrap.appendChild(h(doc, 'div', { class: 'place', text: 'Wetter nicht verfügbar' }));
                    return;
                }
                const [icon, label] = WEATHER[d.current.weather_code] ?? ['🌡️', ''];
                wrap.appendChild(h(doc, 'div', { class: 'now' }, [
                    h(doc, 'span', { text: icon }),
                    h(doc, 'span', { text: `${Math.round(d.current.temperature_2m)}°` }),
                ]));
                wrap.appendChild(h(doc, 'div', { class: 'place', text: `${el.place ?? ''}${label ? ' · ' + label : ''}` }));
                const daily = d.daily;
                const days = (daily?.time ?? []).slice(0, Number(el.days) || 0).map((t, i) => {
                    const [ic] = WEATHER[daily.weather_code[i] ?? -1] ?? ['🌡️'];
                    // Datum "YYYY-MM-DD" ist ein Kalendertag → in UTC formatieren, damit sich der Tag nicht verschiebt.
                    const wd = i === 0 ? 'Heute' : new Date(t).toLocaleDateString(ctx.locale, { weekday: 'short', timeZone: 'UTC' });
                    const max = Math.round(daily.temperature_2m_max[i] ?? NaN);
                    const min = Math.round(daily.temperature_2m_min[i] ?? NaN);
                    const div = h(doc, 'div');
                    div.append(wd, doc.createElement('br'), ic, doc.createElement('br'), `${max}° / ${min}°`);
                    return div;
                });
                if (days.length)
                    wrap.appendChild(h(doc, 'div', { class: 'days' }, days));
            };
            const cached = weatherCache.get(lat, lon);
            if (cached)
                draw(cached);
            if (!ctx.weather) {
                if (!cached)
                    draw(null);
                return;
            }
            let alive = true;
            const provider = ctx.weather;
            const load = () => {
                provider({ lat, lon })
                    .then((d) => {
                    if (!alive)
                        return;
                    if (d?.current) {
                        weatherCache.set(lat, lon, d);
                        draw(d);
                    }
                    else if (!cached)
                        draw(null);
                })
                    .catch(() => {
                    if (alive && !cached)
                        draw(null);
                });
            };
            load();
            const t = setInterval(load, 15 * 60 * 1000);
            return () => {
                alive = false;
                clearInterval(t);
            };
        },
    },
    web: {
        label: 'Webseite',
        defaults: { url: 'https://', reload: 0, zoom: 100 },
        render(box, el, ctx) {
            const { doc } = ctx;
            if (!/^https?:\/\/./i.test(el.url || ''))
                return placeholder(doc, box, 'Adresse der Webseite eingeben');
            if (ctx.preview)
                return placeholder(doc, box, `🌐 ${el.url}`);
            const z = (Number(el.zoom) || 100) / 100;
            clear(box);
            const f = h(doc, 'iframe', {
                class: 'wl-fill',
                style: `width:${100 / z}%;height:${100 / z}%;transform:scale(${z});transform-origin:0 0`,
                attrs: { src: el.url, allow: 'autoplay; fullscreen', referrerpolicy: 'no-referrer' },
            });
            box.appendChild(f);
            if (!Number(el.reload))
                return;
            const t = setInterval(() => {
                f.src = el.url;
            }, Number(el.reload) * 60 * 1000);
            return () => clearInterval(t);
        },
    },
    camera: {
        label: 'Kamera',
        defaults: { source: 'url', url: '', refresh: 5, fit: 'cover', rtspUrl: '', name: '' },
        render(box, el, ctx) {
            const { doc } = ctx;
            const fit = fitStyle(el.fit);
            if (el.source === 'rtsp')
                return rtspCamera(doc, box, el, { preview: ctx.preview, camera: ctx.camera }, fit);
            if (el.source === 'device') {
                // USB-Kamera direkt am Abspielgerät.
                if (ctx.preview)
                    return placeholder(doc, box, '📷 Kamera am Gerät');
                clear(box);
                const v = h(doc, 'video', { class: 'wl-fill', style: fit, attrs: { muted: true, autoplay: true, playsinline: true } });
                v.muted = true;
                box.appendChild(v);
                let stream = null;
                let alive = true;
                const nav = doc.defaultView?.navigator;
                const gum = nav?.mediaDevices?.getUserMedia?.({ video: true, audio: false });
                if (!gum)
                    placeholder(doc, box, 'Kamera nicht verfügbar');
                else
                    gum
                        .then((s) => {
                        stream = s;
                        if (!alive)
                            return s.getTracks().forEach((t) => t.stop());
                        v.srcObject = s;
                    })
                        .catch(() => placeholder(doc, box, 'Kamera nicht verfügbar'));
                return () => {
                    alive = false;
                    stream?.getTracks().forEach((t) => t.stop());
                };
            }
            // Netzwerkkamera: Bild-Adresse (JPEG wird neu geladen, MJPEG läuft als Stream).
            const url = el.url || '';
            if (!/^https?:\/\/./i.test(url))
                return placeholder(doc, box, 'Adresse der Kamera eingeben');
            clear(box);
            const img = h(doc, 'img', { class: 'wl-fill', style: fit, attrs: { src: url } });
            box.appendChild(img);
            if (!Number(el.refresh))
                return;
            const t = setInterval(() => {
                img.src = url + (url.includes('?') ? '&' : '?') + '_t=' + Date.now();
            }, Number(el.refresh) * 1000);
            return () => clearInterval(t);
        },
    },
    ticker: {
        label: 'Laufschrift',
        defaults: {
            text: '+++ Neue Kurse ab Montag +++ Jetzt Probetraining buchen +++',
            speed: 20,
            size: 5,
            color: '#ffffff',
            bg: '#c8102e',
            bold: true,
        },
        render(box, el, { doc }) {
            clear(box);
            box.appendChild(h(doc, 'div', { class: 'wl-ticker', style: textStyle(el) + typoStyle(el) }, [
                h(doc, 'span', { style: `animation-duration:${Math.max(5, Number(el.speed) || 20)}s`, text: el.text ?? '' }),
            ]));
        },
    },
    schedule: {
        label: 'Kursplan',
        defaults: {
            title: 'Kurse heute',
            size: 3.5,
            color: '#ffffff',
            rows: [
                { time: '09:00', course: 'Yoga', trainer: 'Anna', room: 'Raum 1' },
                { time: '18:00', course: 'Spinning', trainer: 'Tom', room: 'Cycle' },
            ],
        },
        render(box, el, ctx) {
            const { doc } = ctx;
            const now = hhmmNow(ctx);
            const accent = el.accent ? `color:${cssSafe(el.accent)}` : '';
            const rows = (el.rows ?? []).map((r) => h(doc, 'tr', { class: r.time && r.time < now ? 'past' : '' }, [
                h(doc, 'td', { class: 't', text: r.time ?? '', ...(accent ? { style: accent } : {}) }),
                h(doc, 'td', { text: r.course ?? '' }),
                h(doc, 'td', { class: 'm', text: [r.trainer, r.room].filter(Boolean).join(' · ') }),
            ]));
            clear(box);
            box.appendChild(h(doc, 'div', {
                class: 'wl-sched',
                style: `color:${cssSafe(el.color || '#fff')};font-size:${Number(el.size) || 3.5}cqh` + (el.font ? `;${typoStyle({ font: el.font })}` : ''),
            }, [
                h(doc, 'h2', { text: el.title ?? '', ...(accent ? { style: accent } : {}) }),
                h(doc, 'table', {}, rows),
            ]));
        },
    },
    prayer: {
        label: 'Gebetszeiten',
        defaults: {
            source: 'calc', method: 'mwl', asr: 'shafi', highLat: 'auto', place: '', lat: null, lon: null, lang: 'de', view: 'table',
            showHijri: true, showDate: true, showSunrise: true, showIqama: true, showCountdown: true, size: 3.2, color: '#ffffff', accent: '#E0B04B',
        },
        render(box, el, ctx) {
            return renderPrayer(box, el, { ...ctx, fontStack: (f) => FONT_STACKS[f] });
        },
    },
    ...DATA_WIDGETS,
    layout: {
        label: 'Vorlage',
        defaults: { layoutId: '', fit: 'contain' },
        render(box, el, ctx) {
            const { doc } = ctx;
            if (!el.layoutId)
                return placeholder(doc, box, 'Vorlage wählen');
            if (ctx.stack.includes(el.layoutId) || ctx.stack.length >= MAX_EMBED_DEPTH)
                return placeholder(doc, box, 'Vorlage kann sich nicht selbst enthalten');
            const inner = ctx.layout?.(el.layoutId);
            if (!inner)
                return placeholder(doc, box, 'Vorlage nicht verfügbar');
            return embedLayout(box, el, inner, ctx);
        },
    },
};
/** Seitenverhältnis einer Vorlage: Zonenvorlage aus der Referenzgrösse, freie Folie aus der Ausrichtung. */
function aspectOf(l, orientation) {
    if (l.specVersion === 1)
        return [l.refWidth, l.refHeight];
    return orientation === 'portrait' ? [9, 16] : [16, 9];
}
/** Eingebettete Vorlage in ihrem Seitenverhältnis in die Box zeichnen (contain/cover/fill). */
function embedLayout(box, el, inner, ctx) {
    const { doc } = ctx;
    const [W, H] = aspectOf(inner, el.orientation);
    const fit = el.fit ?? 'contain';
    const fn = fit === 'cover' ? 'max' : 'min';
    const size = fit === 'fill' ? 'width:100cqw;height:100cqh'
        : `width:${fn}(100cqw, calc(100cqh * ${W} / ${H}));height:${fn}(100cqh, calc(100cqw * ${H} / ${W}))`;
    clear(box);
    const frame = h(doc, 'div', { style: `position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);${size}` });
    frame.dataset.embed = el.layoutId;
    frame.dataset.aspect = `${W}:${H}`;
    frame.dataset.fit = fit;
    box.appendChild(h(doc, 'div', { class: 'wl-embed' }, [frame]));
    return renderInto(frame, inner, (el.content ?? {}), { ...ctx, stack: [...ctx.stack, el.layoutId] });
}
/**
 * Analoge Uhr (Bahnhofsuhr, Klassisch, Modern minimal, Sport) als Inline-SVG, passt sich jeder Boxgrösse an.
 * Darunter optional Beschriftung (z. B. Ort) und Datum (Anzeige "Uhrzeit und Datum").
 */
function analogClock(box, el, ctx) {
    const { doc } = ctx;
    const face = el.face;
    const seconds = el.secondHand !== false;
    const { svg, hands } = buildFace(doc, face, faceColors(face, el), seconds);
    clear(box);
    const wrap = h(doc, 'div', { class: 'wl-analog' });
    wrap.appendChild(svg);
    const showDate = (el.mode ?? 'both') !== 'time';
    const caption = (el.caption ?? '').trim();
    let cap = null;
    if (showDate || caption) {
        cap = h(doc, 'div', {
            class: 'cap',
            style: `color:${cssSafe(el.color || '#fff')};font-size:${(Number(el.size) || 9) * 0.4}cqh;font-weight:${el.bold ? 600 : 400}`,
        });
        wrap.appendChild(cap);
    }
    box.appendChild(wrap);
    let lastMin = -1;
    return runHands(doc.defaultView, {
        now: ctx.now,
        timeZone: ctx.timeZone,
        motion: FACE_PRESETS[face].motion,
        seconds,
        preview: ctx.preview,
        draw: (t) => {
            setHands(hands, handAngles(t, FACE_PRESETS[face].motion));
            // Beschriftung (Datum) nur einmal pro Minute neu setzen, nicht in jedem Bild.
            if (!cap || t.h * 60 + t.m === lastMin)
                return;
            lastMin = t.h * 60 + t.m;
            const day = showDate ? fmtParts(ctx, ctx.now(), { weekday: 'long', day: 'numeric', month: 'long' }) : '';
            cap.textContent = [caption, day].filter(Boolean).join(' · ');
            svg.setAttribute('aria-label', cap.textContent);
        },
    });
}
/** Überblendende Bildfolge (Slideshow-Baustein und Unter-Playlist einer media-Zone). */
function slideshow(ctx, box, slides, fit, zone) {
    clear(box);
    const imgs = slides.map((s, i) => h(ctx.doc, 'img', { class: i ? '' : 'on', style: fitStyle(fit), attrs: { src: s.src } }));
    box.appendChild(h(ctx.doc, 'div', { class: 'wl-slides' }, imgs));
    reportZone(ctx, zone, slides[0]?.asset ?? null);
    if (imgs.length < 2)
        return;
    let i = 0;
    let t;
    const step = () => {
        imgs[i].classList.remove('on');
        i = (i + 1) % imgs.length;
        imgs[i].classList.add('on');
        reportZone(ctx, zone, slides[i].asset ?? null, true);
        t = setTimeout(step, slides[i].ms);
    };
    t = setTimeout(step, slides[0].ms);
    return () => clearTimeout(t);
}
function makeContext(root, opts) {
    const doc = root.ownerDocument;
    const win = doc.defaultView;
    return {
        doc,
        preview: !!opts.preview,
        resolveAsset: opts.resolveAsset ?? ((s) => s),
        assetKind: opts.assetKind ?? (() => undefined),
        now: opts.now ?? (() => Date.now()),
        timeZone: opts.timeZone,
        locale: opts.locale ?? 'de-DE',
        weather: opts.weather,
        camera: opts.camera ?? win?.disploraqCamera,
        prayerTables: opts.prayerTables ? new Map(opts.prayerTables.map((t) => [t.id, t])) : undefined,
        data: dataResolver(opts.data),
        onZoneMedia: opts.onZoneMedia,
        logoUrl: opts.logoUrl ?? null,
        layout: opts.layout,
        stack: opts.layoutId ? [opts.layoutId] : [],
    };
}
/** Daten je Quelle einmal prüfen (parseDataSet) und für diese Darstellung merken. */
function dataResolver(fn) {
    const memo = new Map();
    return (id) => {
        if (!fn || !id)
            return undefined;
        if (!memo.has(id))
            memo.set(id, parseDataSet(fn(id)) ?? undefined);
        return memo.get(id);
    };
}
/**
 * Feldbelegung aus einer Datenquelle ({ type: 'data' }) für die Darstellung auflösen: Datenbausteine erhalten die
 * Quelle, Text/Laufschrift/Kursplan den Text aus den Daten (wie der Server fürs Manifest).
 */
function resolveBinding(el, c, ctx) {
    if (!c || c.type !== 'data' || isDataElementType(el.type))
        return c;
    const d = ctx.data(c.source);
    const text = d ? bindingToText(c, d, el.type === 'schedule' ? 'rows' : 'text') : null;
    return text === null ? undefined : { type: 'text', value: text };
}
/**
 * Zeichnet ein Layout in `root` und liefert eine Aufräumfunktion (Timer, Streams).
 * specVersion 2/Prototyp: Elemente in Prozent; specVersion 1: Zonen in Referenzpixeln mit Letterboxing.
 */
export function renderLayout(root, layout, opts = {}) {
    const ctx = makeContext(root, opts);
    ensureStyles(ctx.doc);
    return renderInto(root, layout, opts.content ?? {}, ctx);
}
function renderInto(root, layout, content, ctx) {
    clear(root);
    root.classList.add('wl-canvas');
    if (layout.specVersion === 1)
        return renderV1(root, layout, content, ctx.logoUrl, ctx);
    const l = layout;
    const bgSrc = 'bgAsset' in l && l.bgAsset ? ctx.resolveAsset(l.bgAsset) : l.bgSrc;
    root.style.backgroundColor = cssSafe(l.bg || '#000');
    root.style.backgroundImage = bgSrc ? cssUrl(bgSrc) : 'none';
    const font = 'font' in l ? l.font : undefined;
    root.style.fontFamily = font && FONT_STACKS[font] ? FONT_STACKS[font] : '';
    const cleanups = [];
    (l.elements ?? []).map((el) => withContent(el, el.id !== undefined ? resolveBinding(el, content[el.id], ctx) : undefined)).forEach((el, i) => {
        const box = h(ctx.doc, 'div', {
            class: 'wl-el',
            style: `left:${Number(el.x)}%;top:${Number(el.y)}%;width:${Number(el.w)}%;height:${Number(el.h)}%;z-index:${i + 1};` +
                `background:${cssSafe(el.bg || 'transparent')};border-radius:${Number(el.radius) || 0}cqh`,
        });
        if (el.id !== undefined)
            box.dataset.id = el.id;
        root.appendChild(box);
        try {
            const type = WIDGET_TYPES[el.type];
            const c = type?.render(box, el, ctx);
            if (typeof c === 'function')
                cleanups.push(c);
        }
        catch (e) {
            placeholder(ctx.doc, box, `Fehler: ${e.message}`);
        }
    });
    return () => cleanups.forEach((f) => f());
}
/**
 * Inhalt eines Playlist-Eintrags auf ein Element (specVersion 2) anwenden: Text, Bild/Video,
 * Bilderfolge oder Kursplan (Text, eine Zeile je Kurs: "Zeit | Kurs | Trainer | Raum").
 */
export function withContent(el, c) {
    if (!c)
        return el;
    switch (el.type) {
        case 'text':
        case 'ticker':
            return c.type === 'text' ? { ...el, text: c.value } : el;
        case 'image':
        case 'video': {
            if (c.type !== 'media')
                return el;
            const rest = { ...el, asset: c.asset, ...(c.fit ? { fit: c.fit } : {}) };
            delete rest.src;
            return rest;
        }
        case 'slideshow': {
            const assets = c.type === 'media' ? [c.asset] : c.type === 'playlist' ? c.items.map((i) => i.asset) : null;
            if (!assets?.length)
                return el;
            const rest = { ...el, assets, ...((c.type === 'media' || c.type === 'playlist') && c.fit ? { fit: c.fit } : {}) };
            delete rest.srcs;
            return rest;
        }
        case 'schedule':
            return c.type === 'text' ? { ...el, rows: parseScheduleText(c.value) } : el;
        // Datenbausteine: Inhalt wählt die Datenquelle (im Manifest als Text mit der id der Quelle)
        case 'feed':
        case 'calendar':
        case 'table':
            return c.type === 'data' ? { ...el, source: c.source } : c.type === 'text' && c.value.trim() ? { ...el, source: c.value.trim() } : el;
        default:
            return el;
    }
}
/** Kursplan als Text (eine Zeile je Kurs, Spalten mit | getrennt) → Zeilen. */
export function parseScheduleText(v) {
    return v.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).map((l) => {
        const [time = '', course = '', trainer = '', room = ''] = l.split('|').map((x) => x.trim());
        return { time, course, trainer, room };
    });
}
/** Kursplan-Zeilen → Text für das Formular (Gegenstück zu parseScheduleText). */
export function scheduleToText(rows) {
    return rows.map((r) => [r.time ?? '', r.course ?? '', r.trainer ?? '', r.room ?? ''].join(' | ').replace(/( \| )+$/, '')).join('\n');
}
// ---------- specVersion 1 ----------
const FONT_STACK = '"Segoe UI", Roboto, Helvetica, Arial, sans-serif';
function renderV1(root, layout, content, logoUrl, ctx) {
    const W = layout.refWidth;
    const H = layout.refHeight;
    // Letterboxing in Hintergrundfarbe bei abweichendem Seitenverhältnis (Kap. 6.2)
    const bgColor = cssSafe(layout.background?.color || '#000');
    root.style.backgroundColor = bgColor;
    root.style.backgroundImage = 'none';
    const frame = h(ctx.doc, 'div', {
        class: 'wl-frame',
        style: `width:min(100cqw, calc(100cqh * ${W} / ${H}));height:min(100cqh, calc(100cqw * ${H} / ${W}));background-color:${bgColor};` +
            (layout.background?.asset ? `background-image:${cssUrl(ctx.resolveAsset(layout.background.asset))};` : ''),
    });
    frame.dataset.ref = `${W}x${H}`;
    root.appendChild(frame);
    const cqh = (px) => `${+((px / H) * 100).toFixed(4)}cqh`;
    const cleanups = [];
    const zones = [...layout.zones].map((z, i) => ({ z, i })).sort((a, b) => (a.z.z ?? 1) - (b.z.z ?? 1) || a.i - b.i);
    for (const { z: zone } of zones) {
        const box = h(ctx.doc, 'div', { class: 'wl-el' });
        box.dataset.id = zone.id;
        box.dataset.zone = zone.type;
        Object.assign(box.style, {
            left: `${(zone.x / W) * 100}%`,
            top: `${(zone.y / H) * 100}%`,
            width: `${(zone.w / W) * 100}%`,
            height: `${(zone.h / H) * 100}%`,
            zIndex: String(zone.z ?? 1),
        });
        frame.appendChild(box);
        try {
            const c = renderZone(box, zone, content[zone.id], logoUrl, ctx, cqh);
            if (typeof c === 'function')
                cleanups.push(c);
        }
        catch (e) {
            placeholder(ctx.doc, box, `Fehler: ${e.message}`);
        }
    }
    return () => cleanups.forEach((f) => f());
}
function renderZone(box, zone, c, logoUrl, ctx, cqh) {
    const { doc } = ctx;
    switch (zone.type) {
        case 'media': {
            const fit = (c && 'fit' in c && c.fit) || ('fit' in zone ? zone.fit : 'cover');
            if (c?.type === 'media') {
                reportZone(ctx, zone.id, c.asset);
                const src = ctx.resolveAsset(c.asset);
                if (ctx.assetKind(c.asset) === 'video') {
                    const v = h(doc, 'video', {
                        class: 'wl-fill',
                        style: fitStyle(fit),
                        attrs: { src, muted: true, loop: true, autoplay: true, playsinline: true },
                    });
                    v.muted = true;
                    box.appendChild(v);
                    v.play?.()?.catch?.(() => { });
                }
                else
                    box.appendChild(h(doc, 'img', { class: 'wl-fill', style: fitStyle(fit), attrs: { src } }));
                return;
            }
            if (c?.type === 'playlist' && c.items.length) {
                return slideshow(ctx, box, c.items.map((it) => ({ src: ctx.resolveAsset(it.asset), ms: Math.max(2000, it.durationMs ?? 6000), asset: it.asset })), fit, zone.id);
            }
            if (ctx.preview)
                placeholder(doc, box, 'Bild oder Video wählen');
            return;
        }
        case 'text': {
            const st = ('style' in zone ? zone.style : {});
            const value = c?.type === 'text' ? c.value : '';
            if (!value && ctx.preview)
                return placeholder(doc, box, zone.id);
            const family = st.font ? `"${cssSafe(st.font)}", ${FONT_STACK}` : FONT_STACK;
            const clamp = st.maxLines ? `-webkit-line-clamp:${st.maxLines};` : '';
            const inner = h(doc, 'div', { class: st.maxLines ? 'wl-clamp' : '', style: clamp, text: value });
            const outer = h(doc, 'div', {
                class: 'wl-center',
                style: `color:${cssSafe(st.color || '#fff')};font-size:${cqh(st.size ?? 48)};text-align:${cssSafe(st.align || 'left')};` +
                    `font-weight:${st.weight ?? 400};font-family:${family};white-space:pre-line;line-height:1.15;padding:0`,
            }, [inner]);
            box.appendChild(outer);
            if (st.autoShrink)
                autoShrink(box, outer);
            return;
        }
        case 'logo': {
            if (logoUrl)
                box.appendChild(h(doc, 'img', { class: 'wl-fill', style: fitStyle('contain'), attrs: { src: logoUrl, alt: '' } }));
            else if (ctx.preview)
                placeholder(doc, box, 'Logo');
            return;
        }
        case 'clock': {
            const st = ('style' in zone ? zone.style : {});
            const el = h(doc, 'div', {
                class: 'wl-center',
                style: `color:${cssSafe(st.color || '#fff')};font-size:${cqh(st.size ?? 48)};text-align:${cssSafe(st.align || 'left')};` +
                    `font-weight:${st.weight ?? 400};${st.font ? `font-family:"${cssSafe(st.font)}", ${FONT_STACK};` : ''}line-height:1.1;padding:0`,
            });
            box.appendChild(el);
            const fmt = st.format || 'HH:mm';
            const tick = () => {
                el.textContent = formatClock(fmt, ctx.now(), ctx.timeZone, ctx.locale);
            };
            tick();
            const t = setInterval(tick, 1000);
            return () => clearInterval(t);
        }
        default:
            return; // unbekannter Zonentyp: leer lassen
    }
}
/** Verkleinert die Schrift, bis der Text in die Zone passt (autoShrink). */
function autoShrink(box, el) {
    const run = () => {
        let scale = 1;
        const base = el.style.fontSize;
        for (let i = 0; i < 20 && box.scrollHeight > box.clientHeight + 1 && box.clientHeight > 0; i++) {
            scale *= 0.92;
            el.style.fontSize = `calc(${base} * ${scale.toFixed(3)})`;
        }
    };
    const raf = box.ownerDocument.defaultView?.requestAnimationFrame;
    if (raf)
        raf(run);
    else
        run();
}
/**
 * Uhrformat mit Platzhaltern: yyyy, MMMM, MM, dd, d, EEEE, EEE, HH, mm, ss (in der Standort-Zeitzone).
 */
export function formatClock(format, at, timeZone, locale = 'de-DE') {
    const tz = timeZone ? { timeZone } : {};
    const d = new Date(at);
    const parts = new Intl.DateTimeFormat('en-US', {
        ...tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
    }).formatToParts(d);
    const p = (t) => parts.find((x) => x.type === t)?.value ?? '';
    const hour = p('hour') === '24' ? '00' : p('hour');
    const map = {
        yyyy: () => p('year'),
        MMMM: () => d.toLocaleDateString(locale, { ...tz, month: 'long' }),
        MM: () => p('month'),
        dd: () => p('day'),
        d: () => String(Number(p('day'))),
        EEEE: () => d.toLocaleDateString(locale, { ...tz, weekday: 'long' }),
        EEE: () => d.toLocaleDateString(locale, { ...tz, weekday: 'short' }),
        HH: () => hour,
        mm: () => p('minute'),
        ss: () => p('second'),
    };
    return format.replace(/yyyy|MMMM|MM|dd|d|EEEE|EEE|HH|mm|ss/g, (t) => map[t]());
}
/**
 * Alle Medienverweise einer Folie (URLs und sha256), damit der Player sie für den Offline-Betrieb vorhält.
 * Wie im Prototyp; bei specVersion 1 aus den Zoneninhalten.
 */
export function sources(layout, content = {}) {
    const out = [];
    if (layout.specVersion === 1) {
        if (layout.background?.asset)
            out.push(layout.background.asset);
        for (const c of Object.values(content)) {
            if (c.type === 'media')
                out.push(c.asset);
            if (c.type === 'playlist')
                out.push(...c.items.map((i) => i.asset));
        }
        return out;
    }
    const l = layout;
    if (l.bgSrc)
        out.push(l.bgSrc);
    // Inhalte des Playlist-Eintrags (Felder) ersetzen Medien der Elemente
    const elements = (l.elements ?? []).map((el) => withContent(el, el.id !== undefined ? content[el.id] : undefined));
    if ('bgAsset' in l && l.bgAsset)
        out.push(l.bgAsset);
    for (const el of elements) {
        if ('src' in el && el.src)
            out.push(el.src);
        if ('srcs' in el && el.srcs)
            out.push(...el.srcs);
        if ('asset' in el && el.asset)
            out.push(el.asset);
        if ('assets' in el && el.assets)
            out.push(...el.assets);
        // Feste Inhalte einer eingebetteten Vorlage (deren eigene Medien bringt die Vorlage selbst mit)
        if (el.type === 'layout') {
            for (const c of Object.values(el.content ?? {})) {
                if (c.type === 'media')
                    out.push(c.asset);
                if (c.type === 'playlist')
                    out.push(...c.items.map((i) => i.asset));
            }
        }
    }
    return out;
}
/** Nur die sha256-Verweise (für Manifest-`assets`). */
export function assetRefs(layout, content = {}) {
    return [...new Set(sources(layout, content).filter((s) => /^[0-9a-f]{64}$/.test(s)))];
}
//# sourceMappingURL=render.js.map